import {computed, ref, Ref} from "vue";
import {NodeDragEvent} from "@vue-flow/core";
import {Flowchart} from "@/core/interfaces/flowchart/flowchart";
import {flowchartApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";

/**
 * Handle the repositioning of flowchart nodes in edit mode and
 * their persistence to the database. Restricted to admin users.
 *
 * @param data The flowchart data.
 */
const useFlowchartNodePositions = (data: Ref<Flowchart | undefined>) => {
    const {showSuccess, showError} = useToastStore();

    /** Node positions moved in edit mode, keyed by node id, pending persistence. */
    const movedPositions = ref<Record<string, { x: number, y: number }>>({});

    /** Whether the moved positions are currently being persisted. */
    const savingPositions = ref(false);

    /** Whether some nodes have been moved and are pending persistence. */
    const hasMovedNodes = computed(() => Object.keys(movedPositions.value).length > 0);

    /**
     * Track the new position of the dragged nodes, so they can be persisted.
     *
     * @param event The node drag event.
     */
    const handleNodeDragStop = (event: NodeDragEvent) => {
        for (const node of event.nodes) {
            movedPositions.value[node.id] = {x: node.position.x, y: node.position.y};
        }
    };

    /**
     * Forget the moved positions without persisting them.
     */
    const resetMovedPositions = () => {
        movedPositions.value = {};
    };

    /**
     * Persist the moved node positions to the database.
     */
    const saveNodePositions = () => {
        if (!data.value || savingPositions.value) {
            return;
        }

        const nodes = Object.entries(movedPositions.value)
            .map(([id, position]) => ({id: Number(id), position}));

        savingPositions.value = true;

        flowchartApi().updateNodePositions(data.value.id, nodes)
            .then(() => {
                for (const node of data.value!.nodes) {
                    const moved = movedPositions.value[node.id];

                    if (moved) {
                        node.position = JSON.stringify(moved);
                    }
                }

                movedPositions.value = {};
                showSuccess({content: 'Les positions des nœuds ont été enregistrées'});
            })
            .catch(() => {
                showError({content: 'Une erreur est survenue lors de l\'enregistrement des positions'});
            })
            .finally(() => {
                savingPositions.value = false;
            });
    };

    return {
        hasMovedNodes,
        savingPositions,
        handleNodeDragStop,
        resetMovedPositions,
        saveNodePositions
    };
};

export default useFlowchartNodePositions;
